import React, { useCallback, useEffect, useState } from 'react';
import { gate } from '../permissions/access';
import { newServices } from '../../_services/newservices.service';
import { loadTeam } from '../onboarding/setup/data/menu';
import toast from '../shared/Toast';
import './DiarySharePanel.css';

/**
 * Settings › Sharing › Services: who is on the salon's services, and why not.
 *
 * There is no per-person service sharing endpoint (diary sharing has one).
 * What the server does tell us, per person, is whether the salon's catalogue
 * can hold their services (hairdresser-staff canHoldServices, or failing that
 * whether they're on service/hairdresserList) and why not
 * (servicesBlockedReason: 'own_menu' | 'invite_pending'). loadTeam already
 * reads both for setup, so this shows the same answer setup acts on.
 *
 * The switch is the signed-in person's own shareService, the same setting as
 * the old Services page Service settings panel (hidden since Oct 2026).
 */

const CheckIcon = () => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><path d="M2.5 7.5L5.5 10.5L11.5 3.5" /></svg>
);
const CrossIcon = () => (
    <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M3.5 3.5L10.5 10.5M10.5 3.5L3.5 10.5" /></svg>
);

const initials = (name) => (name || '?').trim().split(/\s+/).map((n) => n[0]).join('').toUpperCase().slice(0, 2);
const firstName = (name) => (name || '').trim().split(/\s+/)[0] || 'them';

// One person's line: on the salon's services or not, and the server's reason.
function statusOf(p) {
    const on = p.holds !== false && p.canHold !== false;
    if (on) return { on: true, text: p.se ? 'Uses the salon’s services' : 'On the salon’s services' };
    if (p.heldBackBecause === 'own_menu') return { on: false, text: 'Runs their own service menu', managedBy: firstName(p.name) };
    if (p.heldBackBecause === 'invite_pending') return { on: false, text: 'Hasn’t accepted their invite yet' };
    return { on: false, text: 'Can’t go on the salon’s services yet. Check their profile in Team.' };
}

async function readShareService() {
    const res = await newServices.listServices({});
    const rd = res && res.data && res.data.responseData;
    if (!rd || rd.shareService == null) return null;
    return Number(rd.shareService) === 1 ? 1 : 0;
}

export default function ServiceSharingSection({ user }) {
    const hairdresser = user?.userInfo?.hairdresser || user?.userInfo?.hairdresserInfo;
    const isManager = hairdresser?.isSalonOwner === 1 || gate('settings', false);
    const isSelfEmployed = hairdresser?.employmentTypeId === 2;
    const myName = user?.userInfo?.name || '';

    const [share, setShare] = useState(null);
    const [team, setTeam] = useState(null);
    const [typed, setTyped] = useState(true);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [teamError, setTeamError] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        const [s, t] = await Promise.all([
            readShareService().catch(() => null),
            isManager ? loadTeam(myName).catch(() => null) : Promise.resolve(null),
        ]);
        setShare(s);
        if (t) { setTeam(t.people.filter((p) => !p.me)); setTyped(t.typed); setTeamError(false); }
        else if (isManager) setTeamError(true);
        setLoading(false);
    }, [isManager, myName]);

    useEffect(() => { load(); }, [load]);

    const toggle = async () => {
        if (saving || share == null) return;
        const target = share === 1 ? 0 : 1;
        setSaving(true);
        try {
            const res = await newServices.serviceShareSetting({ shareService: target });
            if (!res || !res.data || res.data.statusCode !== 1) throw new Error('refused');
            // Read back before it counts: the setting endpoint can say OK either way.
            await load();
        } catch (e) {
            toast.error('That didn’t save. Try again in a moment.');
        }
        setSaving(false);
    };

    const Toggle = ({ checked, onChange, disabled }) => (
        <button type="button" className={'ds-toggle' + (checked ? ' ds-toggle--on' : '')} onClick={onChange} disabled={disabled} aria-pressed={!!checked}>
            <span className="ds-toggle-knob" />
        </button>
    );

    const onCount = team ? team.filter((p) => statusOf(p).on).length : 0;

    return (
        <div className="dsp-page-content">
            <div className="dsp-page-intro">
                <p className="dsp-page-desc">
                    {isManager
                        ? 'See who is on the salon’s services and why anyone isn’t, so you can sort it without guessing.'
                        : 'Choose whether you share your services and categories with the rest of the team.'}
                </p>
            </div>

            {loading && share == null && !team && <div className="dsp-loading">Loading...</div>}

            {share != null && (
                <div className="dsp-bulk-card" style={{ marginBottom: 24 }}>
                    <div className="dsp-bulk-text">
                        <span className="dsp-bulk-title">
                            {isManager && !isSelfEmployed ? 'Share services and categories with self-employed staff' : 'Share services and categories with other staff'}
                        </span>
                        <span className="dsp-bulk-desc">
                            {isManager && !isSelfEmployed
                                ? (share === 1
                                    ? 'Self-employed team members can use the salon’s services instead of running their own menu.'
                                    : 'Self-employed team members run their own service menus.')
                                : (share === 1 ? 'Your services and categories are shared with the team.' : 'Your services and categories are yours alone.')}
                        </span>
                    </div>
                    <Toggle checked={share === 1} onChange={toggle} disabled={saving} />
                </div>
            )}

            {isManager && teamError && (
                <p className="dsp-manager-note">We couldn&rsquo;t load your team just now. Try again in a moment.</p>
            )}

            {isManager && team && team.length > 0 && (
                <>
                    <p className="beu-section-label" style={{ margin: '24px 0 6px' }}>Team members</p>
                    <p className="dsp-manager-note" style={{ margin: '0 0 14px' }}>
                        {onCount} of {team.length} {team.length === 1 ? 'team member is' : 'team members are'} on the salon&rsquo;s services.
                    </p>
                    <div className="settings-card" style={{ padding: '4px 24px' }}>
                        {team.map((p) => {
                            const st = statusOf(p);
                            const role = p.owner ? 'SALON OWNER' : !typed ? '' : p.se ? 'SELF-EMPLOYED' : 'EMPLOYED';
                            return (
                                <div key={p.id} className="dsp-staff-row">
                                    <div className="dsp-staff-header">
                                        <div className="dsp-staff-avatar">{initials(p.name)}</div>
                                        <div className="dsp-staff-meta">
                                            <span className="dsp-staff-name">{p.name}</span>
                                            {role && <span className="dsp-staff-role">{role}</span>}
                                        </div>
                                    </div>
                                    <div className="dsp-staff-controls">
                                        {st.managedBy && (
                                            <div className="dsp-readonly-row">
                                                <span className="dsp-readonly-note">Managed by {st.managedBy}</span>
                                            </div>
                                        )}
                                        <div className={`dsp-reciprocity ${st.on ? 'dsp-reciprocity--yes' : 'dsp-reciprocity--no'}`}>
                                            {st.on ? <CheckIcon /> : <CrossIcon />} <span>{st.text}</span>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                </>
            )}
        </div>
    );
}
